import type { DataTableColumn } from '../../design-system/components/DataTable';
import { StatusChip } from '../../design-system/components/StatusChip';
import type { TaskState } from '../../api/rmf-client';
import type { LayoutColumnSpec } from '../../state/table-layout';
import { formatDateTime, formatDuration } from '../../utils/date-format';
import { TaskElapsed, TaskRemaining } from './TaskTiming';
import {
  REMAINING_TOOLTIP,
  TASK_TIME_TOOLTIP,
  taskElapsedSortValue,
  taskRemainingSortValue,
} from './task-timing';
import { TaskQuietBadge } from './TaskQuietBadge';
import { TaskLocationCell } from './TaskLocationCell';
import { TaskTypeCell } from './TaskTypeCell';
import { taskTypeText } from './clean-modes';
import type { OlderTask } from './older-tasks';
import type { TaskDistanceView } from '../../api/task-distances';
import {
  cleaningTimeCell,
  cleaningTimeSortValue,
  distanceCell,
  distanceSortValue,
  distanceStillGrowing,
  type DistanceCell,
} from './task-distance';

/**
 * Every column the Tasks table can show, in ONE place.
 *
 * The ids are STABLE: they are what a person's saved layout stores
 * (`UserPreferences.tableLayouts.tasks`), so renaming one silently
 * resets that column for everyone who had placed it. Change a label
 * freely; never an id. A column added later just appears -- at its
 * place in this list, with its `defaultVisible` -- for people whose
 * saved layout predates it (`resolveTableLayout`).
 *
 * THE ORDER HERE IS THE DEFAULT ORDER. Akash's call on 2026-10-20: the
 * staff using this read "when, what state, who, what, where", so
 * Submitted, Status, Started, Robot, Type, Location lead and are shown;
 * Duration, Task ID and Triggered By stay one tick away under Columns
 * rather than taking width on an iPad from the columns read every day.
 *
 * Task time and Est. time remaining (2026-10-22, sg-nomad-01) come
 * straight after Started and are SHOWN: the two clocks staff asked for.
 * Est. time remaining keeps the old Duration column's id (`dur`) -- it
 * is that column, renamed and narrowed to one meaning -- so a saved
 * layout keeps its place and its shown/hidden choice; Task time is new
 * and lands after a saved layout's own Started, shown.
 *
 * Distance and Cleaning time (2026-10-21) come straight after Location:
 * Distance SHOWN -- the figure the human workload a robot saved is read
 * from -- and Cleaning time one tick away beside it. A saved layout that
 * predates them gets Distance after its own Location, visible, and
 * Cleaning time after that, hidden (`resolveTableLayout`'s rule for an
 * id the layout has never mentioned).
 *
 * The CSV export does NOT read this list -- see `exportCsv`.
 */
export type TaskColumnId =
  | 'submitted'
  | 'status'
  | 'start'
  | 'elapsed'
  | 'dur'
  | 'robot'
  | 'type'
  | 'location'
  | 'distance'
  | 'cleaning'
  | 'id'
  | 'by';

export interface TaskColumnSpec extends LayoutColumnSpec {
  id: TaskColumnId;
  /** The name in the Columns control, and the label each value carries
   *  when a phone stacks a row into a card. */
  label: string;
}

export const TASK_COLUMN_SPECS: readonly TaskColumnSpec[] = [
  { id: 'submitted', label: 'Submitted', defaultVisible: true, hideable: true },
  // The one column that cannot go: a list of jobs with no state on it
  // cannot answer the only question everybody brings to it.
  { id: 'status', label: 'Status', defaultVisible: true, hideable: false },
  { id: 'start', label: 'Started', defaultVisible: true, hideable: true },
  { id: 'elapsed', label: 'Task time', defaultVisible: true, hideable: true },
  { id: 'dur', label: 'Est. time remaining', defaultVisible: true, hideable: true },
  { id: 'robot', label: 'Robot', defaultVisible: true, hideable: true },
  { id: 'type', label: 'Type', defaultVisible: true, hideable: true },
  { id: 'location', label: 'Location', defaultVisible: true, hideable: true },
  { id: 'distance', label: 'Distance', defaultVisible: true, hideable: true },
  { id: 'cleaning', label: 'Cleaning time', defaultVisible: false, hideable: true },
  { id: 'id', label: 'Task ID', defaultVisible: false, hideable: true },
  { id: 'by', label: 'Triggered By', defaultVisible: false, hideable: true },
];

const CALENDAR_PREFIX = 'calendar:';
const FAVORITE_PREFIX = 'favorite:';

/**
 * `TaskState.triggeredBy` is RMF's own `booking.requester`, round-
 * tripped verbatim (see `rmf_bridge.py::_on_task_state_update`). It's
 * only ever set by OUR dispatch route, so its absence is a real,
 * meaningful signal — the task was created some other way (a direct
 * RMF API caller, the Open-RMF oracle dashboard, a task the sim
 * scheduled on its own, ...), not a bug to paper over with a guess.
 */
export function triggeredByLabel(triggeredBy: string | undefined): { text: string; title?: string } {
  if (!triggeredBy) {
    return {
      text: 'API',
      // Read by the front desk: no "RMF", "API call" or "oracle" in the
      // hover (review BC-F10, 2026-10-31). The cell's own word is kept.
      title: 'Not started from this console: it came from another system, or the robots’ own scheduling.',
    };
  }
  if (triggeredBy.startsWith(CALENDAR_PREFIX)) {
    return { text: `Calendar (${triggeredBy.slice(CALENDAR_PREFIX.length)})` };
  }
  if (triggeredBy.startsWith(FAVORITE_PREFIX)) {
    // The stored prefix keeps its US spelling; the words on screen are
    // British, as everywhere else in the console (2026-10-31).
    return { text: `Favourite (${triggeredBy.slice(FAVORITE_PREFIX.length)})` };
  }
  return { text: triggeredBy };
}

/**
 * What the "Submitted" column ACTUALLY means, said out loud in the
 * header tooltip because the honest answer is not the obvious one.
 *
 * `TaskStateView.submittedAtMs` is not the moment an operator pressed
 * Dispatch. RMF's own `booking.unix_millis_earliest_start_time` — the
 * field that would carry that — was confirmed live to be a MONOTONIC
 * COUNTER rather than wall-clock epoch ms (`rmf_bridge.py:850-867`),
 * so the worker's `_plausible_unix_ms()` rejects it and substitutes
 * its own receive time instead (`rmf_bridge.py:2060-2062`):
 *
 *     "submitted_at_ms": (
 *         _plausible_unix_ms(booking.get("unix_millis_earliest_start_time"))
 *         or _now_ms()
 *     ),
 *
 * i.e. this is "first seen by the platform". For a task dispatched
 * from this console the two are within about a second, so the column
 * is useful; for a task RMF scheduled on its own, or one that reached
 * the projector late, it is a receive time and nothing more. Never
 * present it as an audit-grade "issued at" — the only field that
 * could be that is `command_intents.submitted_at_ms`, which exists
 * only for tasks dispatched through this console and is not on this
 * screen's read path.
 *
 * The drawer's own Submitted row carries the same sentence; keep the
 * two in step if either is reworded. The why above stays here: the
 * front desk reads the hover, so it says only what the time is (review
 * BC-F10, 2026-10-31).
 */
export const SUBMITTED_TOOLTIP =
  'When the system first saw this task, not when someone pressed Dispatch. For a task sent from this console the two are within about a second.';

/** Takes any string: the older view's state comes off the durable record
 *  as text, and anything unexpected there reads as idle. */
export function statusKind(s: string): 'idle' | 'active' | 'warning' | 'error' | 'completed' {
  if (s === 'queued') return 'idle';
  if (s === 'active') return 'active';
  if (s === 'completed') return 'completed';
  if (s === 'cancelled') return 'warning';
  if (s === 'failed') return 'error';
  return 'idle';
}

/** A distance or cleaning-time figure, with its hover note when it has
 *  one (a lower bound, or a total still growing). */
function distanceText(cell: DistanceCell) {
  return <span title={cell.title}>{cell.text}</span>;
}

/**
 * The table's column definitions, by id.
 *
 * `locationSortValue` is the one input that is not on the row: Location
 * sorts by what its cell shows, and for a queued row that comes from a
 * dispatch request the cell fetched itself (see `TaskLocationCell`) --
 * which the screen reads when the person picks the sort, and which is
 * `null` for a row whose request had not been read by then.
 */
export function taskColumnDefs(ctx: {
  locationSortValue: (t: TaskState) => string | null;
  /** A live row's distance totals (`useTaskDistances`), undefined until
   *  any have arrived for it. */
  distanceOf: (t: TaskState) => TaskDistanceView | undefined;
}): Record<TaskColumnId, DataTableColumn<TaskState>> {
  // A total still growing says so on hover; an ended task's last running
  // total (its finals never arrived) is simply the number.
  const running = (t: TaskState) => distanceStillGrowing(t.status);
  return {
    submitted: {
      id: 'submitted',
      header: <span title={SUBMITTED_TOOLTIP}>Submitted</span>,
      headerLabel: 'Submitted',
      cell: (t) => formatDateTime(t.submittedAtMs),
      sortable: true,
      // Unset sorts BELOW every real timestamp rather than above it:
      // a record with no submit time is the least informative row, not
      // the oldest one.
      sortValue: (t) => t.submittedAtMs ?? -1,
    },
    status: {
      id: 'status',
      header: 'Status',
      cell: (t) => (
        <>
          <StatusChip status={statusKind(t.status)} label={t.status} pulse={t.status === 'active'} />
          {/* An orphaned task keeps a perfectly healthy-looking chip
              forever (`patrol.dispatch-48`, 45 h "active"). The badge
              is the only thing on the row that can contradict it. */}
          <TaskQuietBadge task={t} />
        </>
      ),
      sortable: true,
      sortValue: (t) => t.status,
    },
    start: {
      id: 'start',
      header: 'Started',
      cell: (t) => formatDateTime(t.startedAt),
      sortable: true,
      sortValue: (t) => t.startedAt ?? '',
    },
    robot: {
      id: 'robot',
      header: 'Robot',
      cell: (t) => t.assignedRobot ?? '—',
      sortable: true,
      sortValue: (t) => t.assignedRobot ?? '',
    },
    type: {
      id: 'type',
      header: 'Type',
      cell: (t) => <TaskTypeCell task={t} />,
      sortable: true,
      sortValue: (t) => t.type,
    },
    location: {
      id: 'location',
      header: 'Location',
      cell: (t) => <TaskLocationCell task={t} />,
      sortable: true,
      sortValue: ctx.locationSortValue,
    },
    distance: {
      id: 'distance',
      header: 'Distance',
      cell: (t) => distanceText(distanceCell(ctx.distanceOf(t), running(t))),
      sortable: true,
      sortValue: (t) => distanceSortValue(ctx.distanceOf(t)),
    },
    cleaning: {
      id: 'cleaning',
      header: 'Cleaning time',
      cell: (t) => distanceText(cleaningTimeCell(ctx.distanceOf(t), running(t))),
      sortable: true,
      sortValue: (t) => cleaningTimeSortValue(ctx.distanceOf(t)),
    },
    elapsed: {
      id: 'elapsed',
      header: <span title={TASK_TIME_TOOLTIP}>Task time</span>,
      headerLabel: 'Task time',
      cell: (t) => <TaskElapsed task={t} />,
      sortable: true,
      sortValue: taskElapsedSortValue,
    },
    dur: {
      id: 'dur',
      header: <span title={REMAINING_TOOLTIP}>Est. time remaining</span>,
      headerLabel: 'Est. time remaining',
      cell: (t) => <TaskRemaining task={t} />,
      sortable: true,
      sortValue: taskRemainingSortValue,
    },
    id: {
      id: 'id',
      header: 'Task ID',
      cell: (t) => <code>{t.id}</code>,
      sortable: true,
      sortValue: (t) => t.id,
    },
    by: {
      id: 'by',
      header: 'Triggered By',
      cell: (t) => {
        const { text, title } = triggeredByLabel(t.triggeredBy);
        return <span title={title}>{text}</span>;
      },
      sortable: true,
      sortValue: (t) => triggeredByLabel(t.triggeredBy).text,
    },
  };
}

/**
 * The older view's own first column: when the job finished. Not in
 * `TASK_COLUMN_SPECS`, so not in anyone's layout, and always shown there.
 *
 * WHY IT LEADS AND SORTS. The older list is paged by finish time, newest
 * first (`/history/task_completions`), and each "Load older tasks" adds
 * the jobs that finished before the ones on screen. Sorted by Submitted
 * instead, a page could land ABOVE rows already read: a job that queued
 * for hours behind a long clean finishes late and arrives on an early
 * page, while one submitted after it but finished sooner arrives on the
 * next -- and a charge task that sat queued for days sinks to the bottom
 * of a page whose neighbours are weeks newer, where it reads as the end
 * of the record. In finish order, every new page goes below the last.
 *
 * Not offered to the live table: most of its rows have not finished.
 */
export const OLDER_FINISHED_COLUMN: DataTableColumn<OlderTask> = {
  id: 'finished',
  header: 'Finished',
  cell: (t) => formatDateTime(t.completedAtMs),
  sortable: true,
  sortValue: (t) => t.completedAtMs,
};

/**
 * The SAME columns for the older view's rows, by the same ids, so one
 * person's layout -- which columns, in which order -- applies to both
 * lists. `Record<TaskColumnId, ...>` is the guard: a column added to the
 * registry does not compile until it says what an older row shows.
 *
 * An older row is the durable summary, not the live record, so a few
 * cells are simpler than their live twins: the status has no quiet badge
 * (nothing about a finished task is expected to move), the duration is
 * fixed, and Location reads the places stored with the task instead of
 * fetching anything -- a dash when none were stored.
 */
export function olderTaskColumnDefs(): Record<TaskColumnId, DataTableColumn<OlderTask>> {
  return {
    submitted: {
      id: 'submitted',
      header: <span title={SUBMITTED_TOOLTIP}>Submitted</span>,
      headerLabel: 'Submitted',
      cell: (t) => formatDateTime(t.submittedAtMs),
      sortable: true,
      sortValue: (t) => t.submittedAtMs,
    },
    status: {
      id: 'status',
      header: 'Status',
      cell: (t) => <StatusChip status={statusKind(t.status)} label={t.status} />,
      sortable: true,
      sortValue: (t) => t.status,
    },
    start: {
      id: 'start',
      header: 'Started',
      cell: (t) => formatDateTime(t.startedAtMs),
      sortable: true,
      sortValue: (t) => t.startedAtMs ?? -1,
    },
    robot: {
      id: 'robot',
      header: 'Robot',
      cell: (t) => t.assignedRobot ?? '—',
      sortable: true,
      sortValue: (t) => t.assignedRobot ?? '',
    },
    type: {
      id: 'type',
      header: 'Type',
      cell: (t) => taskTypeText(t.type, t.cleanMode),
      sortable: true,
      sortValue: (t) => t.type,
    },
    location: {
      id: 'location',
      header: 'Location',
      cell: (t) => (
        <span
          title={t.location && t.location.full !== t.location.text ? t.location.full : undefined}
        >
          {t.location?.text ?? '—'}
        </span>
      ),
      sortable: true,
      sortValue: (t) => t.location?.text ?? '',
    },
    // From the completion record's own copy of the totals; a dash for a
    // job that finished before counting began.
    distance: {
      id: 'distance',
      header: 'Distance',
      cell: (t) => distanceText(distanceCell(t.distance)),
      sortable: true,
      sortValue: (t) => distanceSortValue(t.distance),
    },
    cleaning: {
      id: 'cleaning',
      header: 'Cleaning time',
      cell: (t) => distanceText(cleaningTimeCell(t.distance)),
      sortable: true,
      sortValue: (t) => cleaningTimeSortValue(t.distance),
    },
    elapsed: {
      id: 'elapsed',
      header: <span title={TASK_TIME_TOOLTIP}>Task time</span>,
      headerLabel: 'Task time',
      cell: (t) => (t.durationSec == null ? '' : formatDuration(t.durationSec)),
      sortable: true,
      sortValue: (t) => t.durationSec ?? -1,
    },
    // Every older row has ended: nothing remains.
    dur: {
      id: 'dur',
      header: <span title={REMAINING_TOOLTIP}>Est. time remaining</span>,
      headerLabel: 'Est. time remaining',
      cell: () => '—',
      sortable: false,
    },
    id: {
      id: 'id',
      header: 'Task ID',
      cell: (t) => <code>{t.id}</code>,
      sortable: true,
      sortValue: (t) => t.id,
    },
    by: {
      id: 'by',
      header: 'Triggered By',
      cell: (t) => {
        const { text, title } = triggeredByLabel(t.triggeredBy);
        return <span title={title}>{text}</span>;
      },
      sortable: true,
      sortValue: (t) => triggeredByLabel(t.triggeredBy).text,
    },
  };
}
